import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import {
  SakuraFlower,
  CherryBranchImg,
  JapaneseBadge,
} from "../components/SakuraDecorations";

export default function JapanskAkupunkturPage() {
  return (
    <>
      <Helmet>
        <title>Japansk Akupunktur i Risskov | Blid Behandling</title>
        <meta
          name="description"
          content="Japansk akupunktur er en særlig nænsom behandlingsform med ultrafine nåle. Ideel til sensitive personer og børn hos Kita Akupunktur i Risskov."
        />
        <meta property="og:title" content="Japansk Akupunktur i Risskov | Blid Behandling" />
        <meta
          property="og:description"
          content="Japansk akupunktur er en særlig nænsom behandlingsform med ultrafine nåle. Ideel til sensitive personer og børn hos Kita Akupunktur i Risskov."
        />
        <link rel="canonical" href="https://kita-akupunktur.dk/japansk-akupunktur/" />
        {/* Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalSpecialty",
            "name": "Japansk akupunktur - Keiraku Chiryo",
            "provider": {
              "@type": "MedicalClinic",
              "name": "Kita Akupunktur",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Kantorparken 11",
                "addressLocality": "Risskov",
                "postalCode": "8240",
                "addressRegion": "Aarhus",
                "addressCountry": "DK",
              },
            },
          })}
        </script>
      </Helmet>

      {/* Header with Sakura Petals & Glass Pill */}
      <section className="bg-gradient-to-b from-[#FFF5EE] via-[#FFFAF7] to-[#FFF5EE] py-14 lg:py-20 border-b border-[#ffe0cf]/80 relative z-20 overflow-hidden">
        {/* Botanical Cherry Branch Accent from /design-elements/ (cherry-1.png) */}
        <div className="absolute -top-6 -right-6 w-56 sm:w-72 opacity-60 pointer-events-none z-10">
          <CherryBranchImg index={1} flip={true} className="w-full h-auto" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-3 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
            <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
            <span>KEIRAKU CHIRYO • JAPANSK MERIDIANTERAPI</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#593427]">
            Japansk akupunktur
          </h1>
          <p className="text-base sm:text-lg text-[#7a5b4f] mt-3 max-w-2xl">
            Nænsom meridianterapi (Keiraku Chiryo) med ekstremt tynde nåle, puls- og mavediagnose.
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16 space-y-14">
        {/* Section 1: Top Split Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left: 5 Fordele */}
          <div className="lg:col-span-7 space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-[#ffe0cf]/60 border border-[#ffe0cf] flex items-center justify-center text-[#d74814] shrink-0">
                <SakuraFlower className="w-4 h-4" />
              </div>
              <div>
                <span className="text-xs uppercase tracking-wider font-semibold text-[#d74814]">
                  Ekstremt tynde nåle & høj præcision
                </span>
                <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] mt-0.5">
                  5 fordele ved japansk akupunktur med rolig tilgang
                </h2>
              </div>
            </div>

            <p className="text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
              En kraftig nåleoplevelse kan gøre behandlingen svær at slappe af i, især hvis du har sensitiv hud eller tidligere har været utryg ved nåle.
            </p>

            <p className="text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
              Med japansk akupunktur bruger jeg ekstremt tynde nåle på 0,12 mm, ofte med indføringshylster, så nålene næsten ikke mærkes. Ved behov kan jeg også bruge kontaktnåle, som kun berører huden uden at trænge ind.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {[
                { text: "Ekstremt tynde nåle gør behandlingen velegnet til sensitiv hud" },
                { text: "Nålen indføres ofte kun 1 til 1,5 mm i huden" },
                { text: "Kontaktnåle kan bruges, hvis huden ikke skal penetreres" },
                { text: "Puls og mavediagnose hjælper med at vurdere kroppens ubalance" },
                { text: "Behandlingen tilpasses både børn, voksne og kroppens reaktioner" },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className={`glass-pill p-3.5 rounded-2xl border border-[#ffe0cf] flex items-start space-x-3 hover:bg-white transition-all ${
                    idx === 4 ? "sm:col-span-2" : ""
                  }`}
                >
                  <SakuraFlower className="w-4 h-4 text-[#d74814] mt-0.5 shrink-0" />
                  <span className="text-[#593427] text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            <p className="text-[#7a5b4f] leading-relaxed text-sm sm:text-base pt-1">
              Japansk akupunktur er særligt relevant, hvis du tidligere har været utryg ved nåle eller reagerer kraftigt på berøring. Hos{" "}
              <Link to="/" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b] hover:decoration-[#d74814] transition-colors">
                Kita Akupunktur
              </Link>{" "}
              bruger jeg blandt andet hænder, puls og mave til at finde den retning, behandlingen skal tage, før nålene sættes.
            </p>
          </div>

          {/* Right: Meridianterapi Card */}
          <div className="lg:col-span-5 space-y-6">
            <div className="glass-card p-6 sm:p-8 rounded-3xl border border-[#ffe0cf] shadow-md space-y-4 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-48 opacity-20 pointer-events-none">
                <CherryBranchImg index={3} className="w-full h-auto" />
              </div>

              <div className="flex items-center space-x-2.5 relative z-10">
                <SakuraFlower className="w-5 h-5 text-[#d74814]" />
                <h2 className="text-xl sm:text-2xl font-serif text-[#593427]">
                  Meridianterapi med fokus på hele kroppen
                </h2>
              </div>
              <p className="text-[#7a5b4f] leading-relaxed text-sm sm:text-base relative z-10">
                Keiraku Chiryo betyder meridianterapi, og hos Kita Akupunktur bruges metoden til at vurdere, hvordan puls, mave, smerter, uro og kroppens reaktioner hænger sammen.
              </p>
              <p className="text-[#7a5b4f] leading-relaxed text-sm sm:text-base relative z-10">
                Japansk akupunktur er udviklet over mange århundreder, blandt andet af blinde og svagtseende behandlere i Japan, hvor følesansen fik en central rolle. I stedet for mange eller kraftige nåle bruger jeg få, meget tynde nåle, lav indføring og kontakt med huden, så kroppen kan reagere uden unødigt pres.
              </p>
              <p className="text-[#7a5b4f] leading-relaxed text-sm sm:text-base relative z-10">
                Smerter sidder ikke altid kun dér, hvor de mærkes tydeligst. Mavespændinger, puls, uro, søvn og bevægelighed kan pege på, hvilke punkter der skal arbejdes med, og for nogle kan japansk akupunktur fungere som et naturlig smertestillende supplement.
              </p>

              <div className="pt-3 border-t border-[#ffe0cf] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-2.5 relative z-10">
                <JapaneseBadge label="MERIDIANTERAPI" meaning="Kroppens energibaner i balance" />
                <JapaneseBadge label="JAPANSK RO" meaning="Stressfri, blid akupunktur" />
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Sådan arbejder jeg med japansk akupunktur (4-step Process Cards) */}
        <section className="glass-sakura p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] space-y-6 shadow-xs relative overflow-hidden">
          <div className="absolute -bottom-8 -left-8 w-40 opacity-20 pointer-events-none">
            <CherryBranchImg index={5} className="w-full h-auto" />
          </div>

          <div className="flex items-center space-x-2.5 relative z-10">
            <SakuraFlower className="w-5 h-5 text-[#d74814]" />
            <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
              Sådan arbejder jeg med japansk akupunktur
            </h2>
          </div>
          
          <p className="text-[#7a5b4f] leading-relaxed text-base relative z-10">
            Når du kommer til en behandling, følger vi en rolig og gennemtestet proces:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 relative z-10">
            <div className="bg-white/95 p-5 rounded-2xl border border-[#ffe0cf] shadow-2xs">
              <span className="w-7 h-7 rounded-full bg-[#ffe0cf] text-[#d74814] flex items-center justify-center text-xs font-bold font-serif mb-2">1</span>
              <h3 className="font-serif font-semibold text-[#593427] text-base mb-1">
                1. Samtale om symptomer og historik
              </h3>
              <p className="text-[#7a5b4f] text-xs sm:text-sm">
                Vi taler om dine gener, din hverdag og hvad du ønsker hjælp til.
              </p>
            </div>

            <div className="bg-white/95 p-5 rounded-2xl border border-[#ffe0cf] shadow-2xs">
              <span className="w-7 h-7 rounded-full bg-[#ffe0cf] text-[#d74814] flex items-center justify-center text-xs font-bold font-serif mb-2">2</span>
              <h3 className="font-serif font-semibold text-[#593427] text-base mb-1">
                2. Puls- og mavediagnose (Hara)
              </h3>
              <p className="text-[#7a5b4f] text-xs sm:text-sm">
                Jeg mærker på din puls og foretager en blid berøring af maven for at lokalisere spændinger og meridianmønstre.
              </p>
            </div>

            <div className="bg-white/95 p-5 rounded-2xl border border-[#ffe0cf] shadow-2xs">
              <span className="w-7 h-7 rounded-full bg-[#ffe0cf] text-[#d74814] flex items-center justify-center text-xs font-bold font-serif mb-2">3</span>
              <h3 className="font-serif font-semibold text-[#593427] text-base mb-1">
                3. Nænsom nåleplacering
              </h3>
              <p className="text-[#7a5b4f] text-xs sm:text-sm">
                Udvalgte få punkter stimuleres med de ultra-tynde 0,12 mm nåle. Nålene sidder typisk i 15-25 minutter, mens du slapper af.
              </p>
            </div>

            <div className="bg-white/95 p-5 rounded-2xl border border-[#ffe0cf] shadow-2xs">
              <span className="w-7 h-7 rounded-full bg-[#ffe0cf] text-[#d74814] flex items-center justify-center text-xs font-bold font-serif mb-2">4</span>
              <h3 className="font-serif font-semibold text-[#593427] text-base mb-1">
                4. Eventuel kombination
              </h3>
              <p className="text-[#7a5b4f] text-xs sm:text-sm">
                Behandlingen kan ved behov suppleres med øre-akupunktur eller NADA-punkter for forlænget beroligende effekt.
              </p>
            </div>
          </div>
        </section>

        {/* Section 3: Hvilke tilstande egner metoden sig til? (3-col grid) */}
        <section className="space-y-4 pt-2 border-t border-[#ffe0cf]">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-[#ffe0cf]/60 border border-[#ffe0cf] flex items-center justify-center text-[#d74814] shrink-0">
              <SakuraFlower className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Hvilke tilstande egner metoden sig til?
              </h2>
              <p className="text-[#7a5b4f] text-sm sm:text-base mt-0.5">
                Japansk akupunktur er ideel til en bred vifte af fysiske og psykiske ubalancer, herunder:
              </p>
            </div>
          </div>
          
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
            {[
              "Kroniske og akutte smerter",
              "Stress, udbrændthed og nervøsitet",
              "Søvnløshed og afbrudt søvn",
              "Fordøjelsesproblemer og mavesmerter",
              "Kæbespændinger og hovedpine",
              "Sensitivt nervesystem og overstimulering",
            ].map((item, idx) => (
              <li
                key={idx}
                className="flex items-center p-3.5 rounded-xl glass-pill hover:bg-white transition-all shadow-xs border border-[#ffe0cf]"
              >
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814] mr-2.5 shrink-0" />
                <span className="text-[#593427] text-sm font-medium">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Section: Migræne (id="migraene") */}
        <section
          id="migraene"
          className="scroll-mt-28 glass-sakura p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm relative overflow-hidden space-y-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[#ffe0cf]/70 pb-4">
            <div>
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-2 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>FOKUSOMRÅDE • MIGRÆNE & HOVEDPINE</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Migræne
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2.5 self-start sm:self-auto">
              <Link
                to="/migraene"
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-white border border-[#ffe0cf] text-[#593427] text-sm font-medium hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
              >
                <span>Læs undersiden</span>
                <i className="fa-solid fa-arrow-right ml-1.5 text-xs text-[#d74814]" aria-hidden="true" />
              </Link>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-[#d74814] text-white text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-xs"
              >
                <span>Bestil tid</span>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-7 space-y-4 text-[#7a5b4f] leading-relaxed text-base">
              <p>
                Migræne og kraftig spændingshovedpine er ofte tæt forbundet med et overstimuleret nervesystem, ubalancer i kroppens meridianbaner samt spændinger omkring nakke, tindinger og kæber.
              </p>
              <p>
                Den japanske akupunktur (Keiraku Chiryo) er særligt effektiv ved migræne, fordi den arbejder med ultra-nænsomhed. Mennesker med migræne har typisk en høj sensitivitet, hvor kraftig nålestikning kan virke overvældende. Med ultra-tynde 0,12 mm nåle og overfladisk stimulation beroliges det autonome nervesystem, så kramper i blodkarrene afspændes og anfald forebygges.
              </p>
            </div>

            <div className="lg:col-span-5 bg-white/90 p-5 rounded-2xl border border-[#ffe0cf] space-y-3">
              <h3 className="font-serif font-semibold text-[#593427] text-base flex items-center space-x-2">
                <i className="fa-solid fa-check-circle text-[#d74814]" aria-hidden="true" />
                <span>Fordele ved behandlingen:</span>
              </h3>
              <ul className="space-y-2 text-sm text-[#7a5b4f]">
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Dæmper hyppighed, varighed og intensitet af anfald</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Mindsker ledsagende kvalme, lysfølsomhed og nakkesmerter</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Smertefri og behagelig nålebehandling til sensitive kroppe</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Kan kombineres med beroligende øre-akupunktur</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section: Ondt i kæben (id="ondt-i-kaeben") */}
        <section
          id="ondt-i-kaeben"
          className="scroll-mt-28 bg-white/90 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm relative overflow-hidden space-y-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[#ffe0cf]/70 pb-4">
            <div>
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-2 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>FOKUSOMRÅDE • TYGGEMUSKLER & BIDFUNKTION</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Ondt i kæben
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2.5 self-start sm:self-auto">
              <Link
                to="/ondt-i-kaeben"
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-white border border-[#ffe0cf] text-[#593427] text-sm font-medium hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
              >
                <span>Læs undersiden</span>
                <i className="fa-solid fa-arrow-right ml-1.5 text-xs text-[#d74814]" aria-hidden="true" />
              </Link>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-[#d74814] text-white text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-xs"
              >
                <span>Bestil tid</span>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-7 space-y-4 text-[#7a5b4f] leading-relaxed text-base">
              <p>
                Kæben er et af kroppens mest stressfølsomme områder. Mange bider ubevidst tænderne sammen i løbet af arbejdsdagen eller skærer tænder om natten (bruxisme). Det skaber voldsomt spændingstryk i tyggemusklerne (masseter og temporalis) og belaster kæbeleddet.
              </p>
              <p>
                Typiske symptomer er ømhed ved tygning, klikkende eller knækkende lyde, nedsat gabeevne samt udstrålende smerter mod ører, tindinger, nakke og pande. Med min efteruddannelse i manuel behandling af kæbeproblemer kombineret med japansk akupunktur løsnes muskelspændingerne blidt og præcist.
              </p>
            </div>

            <div className="lg:col-span-5 bg-[#fffaf7] p-5 rounded-2xl border border-[#ffe0cf] space-y-3">
              <h3 className="font-serif font-semibold text-[#593427] text-base flex items-center space-x-2">
                <i className="fa-solid fa-check-circle text-[#d74814]" aria-hidden="true" />
                <span>Hjælp til kæbeproblemer:</span>
              </h3>
              <ul className="space-y-2 text-sm text-[#7a5b4f]">
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Effektiv afspænding af tyggemuskler og kæbeled</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Lindring ved tandpres, tænderskæren og kliklyde</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Mindsker spændingshovedpine udløst fra kæbe og nakke</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Specialiseret tilgang med blid manuel kæbeterapi</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section: Relaterede behandlingsområder */}
        <section className="p-6 sm:p-8 rounded-3xl bg-[#fff5ee] border border-[#ffe0cf] space-y-4">
          <div className="space-y-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#d74814]">
              Relaterede behandlingsområder
            </div>
            <p className="text-base font-serif text-[#593427]">
              Japansk akupunktur kombineres ofte med andre afspændende og smertelindrende behandlinger
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <Link
              to="/migraene"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Migræne & hovedpine →
            </Link>
            <Link
              to="/ondt-i-kaeben"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Ondt i kæben & tandpres →
            </Link>
            <Link
              to="/stress"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Stress & nervesystem →
            </Link>
            <Link
              to="/spaendinger"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Spændinger i nakke & skulder →
            </Link>
            <Link
              to="/ondt-i-ryggen"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Ondt i ryggen & lænd →
            </Link>
            <Link
              to="/led-og-muskelsmerter"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Led- og muskelsmerter →
            </Link>
            <Link
              to="/oere-akupunktur"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Øre akupunktur →
            </Link>
            <Link
              to="/akupunktur"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Klassisk akupunktur →
            </Link>
          </div>
        </section>

        {/* Hurtige genveje */}
        <div className="flex flex-wrap items-center justify-center gap-3 pt-2 text-xs sm:text-sm">
          <Link
            to="/priser"
            className="px-4 py-2 rounded-full bg-[#d74814] text-white font-medium hover:bg-[#593427] transition-colors shadow-2xs"
          >
            Se priser & tilskud fra “danmark”
          </Link>
          <Link
            to="/om-mig"
            className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
          >
            Læs om Mayumis uddannelse i Japan
          </Link>
          <Link
            to="/faq"
            className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
          >
            Samlet FAQ
          </Link>
          <Link
            to="/kontakt"
            className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
          >
            Bestil tid i Risskov
          </Link>
        </div>

        {/* FAQ Section sidst på siden */}
        <section className="pt-8 pb-12 border-t border-[#ffe0cf]/80">
          <FaqSection
            title="Ofte stillede spørgsmål om japansk akupunktur"
            subtitle="Lær mere om den nænsomme Keiraku Chiryo meridianterapi, de ultratynde nåle og den helhedsorienterede tilgang hos Kita Akupunktur."
            faqs={[
              {
                question: "Hvad adskiller japansk akupunktur fra traditionel kinesisk akupunktur?",
                answer: (
                  <>
                    Japansk akupunktur (Keiraku Chiryo) fokuserer på ekstrem nænsomhed, præcision og harmonisering af kroppens meridianer frem for kraftig stimulation. Hvor traditionel kinesisk akupunktur ofte benytter tykkere nåle og dybe stik, anvender japansk akupunktur ultratynde nåle på helt ned til 0,12 mm og overfladisk stikteknik. Mave- og pulsdiagnose indgår løbende gennem hele sessionen. Læs mere under{" "}
                    <Link to="/om-mig" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      om mig
                    </Link>
                    .
                  </>
                ),
              },
              {
                question: "Hvorfor er japansk akupunktur særligt velegnet til sensitive eller nåleængstelige personer?",
                answer:
                  "Den japanske metode er udviklet med en særlig respekt for kroppens finere energisystem og sanseapparat. Nålene er så tynde og indstikket så overfladisk og blidt, at de færreste overhovedet registrerer, hvornår nålen placeres. Hvis du er nervøs for smerter, har nåleskræk eller generelt er sansemæssigt sensitiv, er japansk meridianterapi den mest skånsomme og trygge vej til at opnå akupunkturens dybe helbredende virkning i rolige rammer.",
              },
              {
                question: "Hvad indebærer mavediagnose (Hara) og pulsdiagnose i japansk tradition?",
                answer:
                  "I japansk tradition er maven og pulsen kroppens spejl for de indre organers sundhedstilstand og energistrømme (Ki). Ved blid berøring og palpation af specifikke zoner på maven samt en fintfølende registrering af de radiale pulspositioner på begge håndled kan jeg præcist identificere, hvor der er spændinger, tomhed eller stagnation. Dette danner grundlaget for en skræddersyet behandling, som tilpasses dynamisk undervejs på briksen.",
              },
              {
                question: "Hvilke lidelser og symptomer reagerer særligt positivt på japansk akupunktur?",
                answer: (
                  <>
                    Japansk akupunktur er yderst effektiv ved{" "}
                    <Link to="/stress" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      stress og søvnløshed
                    </Link>
                    ,{" "}
                    <Link to="/migraene" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      migræne og hovedpine
                    </Link>
                    ,{" "}
                    <Link to="/ondt-i-kaeben" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      kæbespændinger
                    </Link>{" "}
                    samt kroniske muskel- og ledsmerter. Da metoden genopretter balancen i hele det autonome nervesystem, oplever mange klienter markant mere energi og dyb ro i hverdagen.
                  </>
                ),
              },
              {
                question: "Hvor mange behandlinger skal jeg forvente, og er der tilskud fra 'danmark'?",
                answer: (
                  <>
                    De fleste oplever en beroligende og balancerende effekt allerede efter første besøg, men for vedvarende resultater anbefales typisk et forløb på 4-6 ugentlige sessioner. Som autoriseret RAB-behandler opfylder jeg alle faglige krav, hvilket betyder, at du kan opnå tilskud fra Sygeforsikring “danmark” samt private sundhedsforsikringer. Se detaljer under{" "}
                    <Link to="/priser" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      priser og tilskud
                    </Link>
                    .
                  </>
                ),
              },
            ]}
          />
        </section>
      </div>
    </>
  );
}
